import { useTranslation } from "react-i18next"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import { useExercisePlan, useDeleteExercisePlan } from "@/api/exercise-plans"
import { Loader2 } from "lucide-react"

interface DeleteExercisePlanDialogProps {
  planId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteExercisePlanDialog({ planId, open, onOpenChange }: DeleteExercisePlanDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const {
    data: plan,
    isLoading: isLoadingPlan,
    error,
  } = useExercisePlan(planId, {
    enabled: !!planId && open,
  })
  const deleteExercisePlan = useDeleteExercisePlan()

  const handleDelete = async () => {
    if (!planId) {
      console.error("No plan ID provided")
      return
    }

    try {
      console.log("Deleting exercise plan with ID:", planId)
      await deleteExercisePlan.mutateAsync(planId)
      onOpenChange(false)
    } catch (error) {
      console.error("Error deleting exercise plan:", error)
      toast({
        title: t("common.error"),
        description: t("exercisePlans.errorDeletingPlan"),
        variant: "destructive",
      })
    }
  }

  // Don't render anything if dialog is not open
  if (!open) {
    return null
  }

  // Show error state
  if (error) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.error")}</AlertDialogTitle>
            <AlertDialogDescription>{t("exercisePlans.errorLoadingPlan")}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 text-destructive">{error instanceof Error ? error.message : "Unknown error"}</div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.close")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  // Show loading state
  if (isLoadingPlan || !plan) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("exercisePlans.deletePlan")}</AlertDialogTitle>
          <AlertDialogDescription>{t("exercisePlans.confirmDelete")}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4">
          <p className="font-medium">{plan.name}</p>
          <p className="text-sm text-muted-foreground">{plan.type}</p>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteExercisePlan.isPending}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleteExercisePlan.isPending}
            className="bg-destructive text-destructive-foreground"
          >
            {deleteExercisePlan.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.deleting")}
              </>
            ) : (
              t("common.delete")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
